/***
 *
 *   THEME
 *   Resolves and applies the colour theme by writing data-theme on <html>,
 *   which flips the CSS variables declared in src/css/tokens.css.
 *
 *   Resolution order: explicit user choice (localStorage) → OS preference →
 *   light. An unset choice keeps following the OS, live.
 *
 *   The initial paint is handled by an inline script in public/index.html so
 *   the page never flashes the wrong theme before React mounts.
 *
 **********/

import { createContext, useCallback, useContext, useEffect, useState } from 'react';

export const THEME_STORAGE_KEY = 'valetify-theme';

const THEMES = ['light', 'dark'];
const MEDIA_QUERY = '(prefers-color-scheme: dark)';

function getStoredTheme() {
    try {
        const stored = localStorage.getItem(THEME_STORAGE_KEY);
        return THEMES.includes(stored) ? stored : null;
    } catch (err) {
        // private browsing / disabled storage — fall through to the OS preference
        return null;
    }
}

function getSystemTheme() {
    return window.matchMedia?.(MEDIA_QUERY).matches ? 'dark' : 'light';
}

function applyTheme(theme) {
    const root = document.documentElement;

    // Two mechanisms, both required. Keep in sync with the pre-paint script in
    // public/index.html and the selectors in src/css/tokens.css:
    //   .dark        -> what Tailwind's `dark:` utilities match. Tailwind 3.2
    //                   only supports the bare 'class' strategy.
    //   data-theme   -> color-scheme, and anything reading the attribute.
    root.classList.toggle('dark', theme === 'dark');
    root.dataset.theme = theme;
}

export const ThemeContext = createContext({
    theme: 'light',
    setTheme: () => {},
    toggleTheme: () => {},
    isDark: false,
});

export function ThemeProvider(props) {
    // hasChoice tracks whether the user picked explicitly; if they haven't,
    // we keep following the OS instead of freezing on whatever it was at load.
    const [hasChoice, setHasChoice] = useState(() => !!getStoredTheme());
    const [theme, setThemeState] = useState(() => getStoredTheme() || getSystemTheme());

    useEffect(() => {
        applyTheme(theme);
    }, [theme]);

    useEffect(() => {
        if (hasChoice) return;

        const mq = window.matchMedia?.(MEDIA_QUERY);
        if (!mq) return;

        const onChange = (e) => {
            const next = e.matches ? 'dark' : 'light';
            applyTheme(next);
            setThemeState(next);
        };

        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, [hasChoice]);

    const setTheme = useCallback((next) => {
        if (!THEMES.includes(next)) return;

        try {
            localStorage.setItem(THEME_STORAGE_KEY, next);
        } catch (err) {
            // not persisting is survivable — the theme still applies this session
        }

        // Applied here rather than only in the effect above: the effect runs
        // after children render, so anything that reads resolved colours during
        // render (the chart canvas) would otherwise see the previous theme.
        applyTheme(next);

        setHasChoice(true);
        setThemeState(next);
    }, []);

    const toggleTheme = useCallback(() => {
        setTheme(theme === 'dark' ? 'light' : 'dark');
    }, [theme, setTheme]);

    return (
        <ThemeContext.Provider
            value={{
                theme: theme,
                setTheme: setTheme,
                toggleTheme: toggleTheme,
                isDark: theme === 'dark',
            }}
            {...props}
        />
    );
}

export const useTheme = () => {
    return useContext(ThemeContext);
};
